import React, { useState, useEffect, useCallback } from 'react';
import { getAlerts, acknowledgeAlert, acknowledgeBulkAlerts, getAlertStats } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Bell, CheckCircle, AlertTriangle, AlertOctagon, Info } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SEVERITY_CONFIG = {
  CRITICAL: { icon: AlertOctagon, color: 'text-red-600', bg: 'bg-red-100' },
  WARNING: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-100' },
  INFO: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-100' }
};

const TYPE_OPTIONS = [
  { value: 'TIEMPO_EXCEDIDO', label: 'Tiempo Excedido' },
  { value: 'REINCIDENCIA', label: 'Reincidencia' },
  { value: 'BAJO_MINIMO', label: 'Bajo Mínimo' },
  { value: 'FOTO_FALTANTE', label: 'Foto Faltante' },
  { value: 'INVENTARIO_INCOMPLETO', label: 'Inventario Incompleto' }
];

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [filters, setFilters] = useState({
    severity: 'all',
    alert_type: 'all',
    acknowledged: 'false'
  });

  useEffect(() => {
    loadAlerts();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadAlerts = useCallback(async () => {
    try {
      const params = {};
      if (filters.severity && filters.severity !== 'all') params.severity = filters.severity;
      if (filters.alert_type && filters.alert_type !== 'all') params.alert_type = filters.alert_type;
      if (filters.acknowledged !== 'all') params.acknowledged = filters.acknowledged === 'true';
      
      const response = await getAlerts(params);
      setAlerts(response.data);
    } catch (error) {
      toast.error('Error cargando alertas');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      const response = await getAlertStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Error cargando estadísticas');
    }
  }, []);

  const handleAcknowledge = async (alertId) => {
    try {
      await acknowledgeAlert(alertId);
      toast.success('Alerta reconocida');
      loadAlerts();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error reconociendo alerta');
    }
  };

  const handleBulkAcknowledge = async () => {
    if (selectedAlerts.length === 0) {
      toast.error('Selecciona al menos una alerta');
      return;
    }
    try {
      await acknowledgeBulkAlerts(selectedAlerts);
      toast.success(`${selectedAlerts.length} alertas reconocidas`);
      setSelectedAlerts([]);
      loadAlerts();
      loadStats();
    } catch (error) {
      toast.error('Error reconociendo alertas');
    }
  };

  const toggleSelectAll = () => {
    const pendingIds = alerts.filter(a => !a.acknowledged_at).map(a => a.id);
    if (selectedAlerts.length === pendingIds.length) {
      setSelectedAlerts([]);
    } else {
      setSelectedAlerts(pendingIds);
    }
  };

  const toggleSelect = (alertId) => {
    setSelectedAlerts(prev => 
      prev.includes(alertId) 
        ? prev.filter(id => id !== alertId)
        : [...prev, alertId]
    );
  };

  const SeverityIcon = ({ severity }) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.INFO;
    const Icon = config.icon;
    return <Icon className={`w-5 h-5 ${config.color}`} />;
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Alertas</h1>
          <p className="text-slate-500">Gestiona y reconoce las alertas del sistema</p>
        </div>
        {selectedAlerts.length > 0 && (
          <Button onClick={handleBulkAcknowledge} data-testid="bulk-acknowledge-btn">
            <CheckCircle className="w-4 h-4 mr-2" />
            Reconocer ({selectedAlerts.length})
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pendientes</p>
                  <p className="text-2xl font-bold text-slate-900 font-['Chivo']">{stats.pending}</p>
                </div>
                <Bell className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Críticas</p>
                  <p className="text-2xl font-bold text-red-700 font-['Chivo']">{stats.by_severity?.critical || 0}</p>
                </div>
                <AlertOctagon className="w-8 h-8 text-red-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Advertencias</p>
                  <p className="text-2xl font-bold text-amber-700 font-['Chivo']">{stats.by_severity?.warning || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-slate-900 font-['Chivo']">{stats.total}</p>
                </div>
                <Info className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={filters.acknowledged} onValueChange={(value) => setFilters({...filters, acknowledged: value})}>
              <SelectTrigger data-testid="filter-acknowledged">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="false">Pendientes</SelectItem>
                <SelectItem value="true">Reconocidas</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.severity} onValueChange={(value) => setFilters({...filters, severity: value})}>
              <SelectTrigger data-testid="filter-severity">
                <SelectValue placeholder="Severidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="CRITICAL">Crítica</SelectItem>
                <SelectItem value="WARNING">Advertencia</SelectItem>
                <SelectItem value="INFO">Info</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.alert_type} onValueChange={(value) => setFilters({...filters, alert_type: value})}>
              <SelectTrigger data-testid="filter-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {TYPE_OPTIONS.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={`alerts-skeleton-${i}`} className="h-12" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10">
                    <Checkbox 
                      checked={selectedAlerts.length > 0 && selectedAlerts.length === alerts.filter(a => !a.acknowledged_at).length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Tienda</TableHead>
                  <TableHead>Mensaje</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow 
                    key={alert.id} 
                    className={alert.acknowledged_at ? 'opacity-60' : ''}
                    data-testid={`alert-row-${alert.id}`}
                  >
                    <TableCell>
                      {!alert.acknowledged_at && (
                        <Checkbox 
                          checked={selectedAlerts.includes(alert.id)}
                          onCheckedChange={() => toggleSelect(alert.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <SeverityIcon severity={alert.severity} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {TYPE_OPTIONS.find(t => t.value === alert.type)?.label || alert.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{alert.store_name || '-'}</TableCell>
                    <TableCell className="max-w-md text-sm text-slate-600">
                      {alert.message}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(alert.created_at), 'dd/MM HH:mm')}
                    </TableCell>
                    <TableCell className="text-right">
                      {alert.acknowledged_at ? (
                        <span className="text-xs text-slate-400">
                          Reconocida por {alert.acknowledged_by_name}
                        </span>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAcknowledge(alert.id)}
                          data-testid={`acknowledge-${alert.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Reconocer
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {alerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No hay alertas para los filtros seleccionados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
